// ─────────────────────────────────────────────────────────────────────────────
// TICKET LIFECYCLE — single source of truth for status flow across
// Admin / Service Manager / Customer Engineer / Vendor views.
// ─────────────────────────────────────────────────────────────────────────────

// Ordered lifecycle (index = stage order, used for progress bars / aging)
export const STATUS_FLOW = [
  'NEW',
  'ASSIGNED_TO_MANAGER',
  'ASSIGNED_TO_CE',
  'ASSIGNED_TO_VENDOR',
  'IN_PROGRESS',
  'WAITING_FOR_PARTS',
  'WORK_COMPLETED',
  'CE_VALIDATION',
  'MANAGER_VALIDATION',
  'RESOLVED',
  'CLOSED',
];

// Side-statuses that can happen at any point and kick the ticket backwards
export const SIDE_STATUSES = ['REJECTED', 'REOPENED'];

export const ALL_STATUSES = [...STATUS_FLOW, ...SIDE_STATUSES];

// Display config for every status — label, colour, bg, dot
export const STATUS_CONFIG = {
  NEW:                  { label: 'New',                 color: '#FF3B30', bg: 'rgba(255,59,48,.08)',  dot: '#FF3B30' },
  ASSIGNED_TO_MANAGER:  { label: 'Awaiting Manager',     color: '#FF3B30', bg: 'rgba(255,59,48,.08)',  dot: '#FF3B30' },
  ASSIGNED_TO_CE:       { label: 'Assigned to CE',       color: '#007AFF', bg: 'rgba(0,122,255,.08)',  dot: '#007AFF' },
  ASSIGNED_TO_VENDOR:   { label: 'Assigned to Vendor',   color: '#5856D6', bg: 'rgba(88,86,214,.08)',  dot: '#5856D6' },
  IN_PROGRESS:          { label: 'In Progress',          color: '#E6870A', bg: 'rgba(255,159,10,.08)', dot: '#FF9F0A' },
  WAITING_FOR_PARTS:    { label: 'Waiting for Parts',    color: '#B45309', bg: 'rgba(180,83,9,.08)',   dot: '#B45309' },
  WORK_COMPLETED:       { label: 'Work Completed',       color: '#0EA5E9', bg: 'rgba(14,165,233,.08)', dot: '#0EA5E9' },
  CE_VALIDATION:        { label: 'CE Validation',        color: '#AF52DE', bg: 'rgba(175,82,222,.08)', dot: '#AF52DE' },
  MANAGER_VALIDATION:   { label: 'Manager Validation',   color: '#8B36B0', bg: 'rgba(139,54,176,.08)', dot: '#8B36B0' },
  RESOLVED:             { label: 'Resolved',             color: '#248A3D', bg: 'rgba(52,199,89,.08)',  dot: '#34C759' },
  CLOSED:               { label: 'Closed',                color: '#636366', bg: 'rgba(142,142,147,.08)',dot: '#8E8E93' },
  REJECTED:             { label: 'Rejected',              color: '#FF3B30', bg: 'rgba(255,59,48,.1)',   dot: '#FF3B30' },
  REOPENED:             { label: 'Reopened',              color: '#FF9F0A', bg: 'rgba(255,159,10,.1)',  dot: '#FF9F0A' },
};

// Back-compat aliases — older tickets created before this lifecycle existed
// used the simple OPEN/ASSIGNED/IN_PROGRESS/RESOLVED/CLOSED set.
const LEGACY_ALIAS = {
  'OPEN':         'NEW',
  'ASSIGNED':     'ASSIGNED_TO_CE',
  'IN PROGRESS':  'IN_PROGRESS', // space variant seen in some older records
};

/** Normalise any stored status string to a canonical key in STATUS_CONFIG */
export function normaliseStatus(status) {
  const s = (status || '').toUpperCase().trim();
  if (STATUS_CONFIG[s]) return s;
  if (LEGACY_ALIAS[s]) return LEGACY_ALIAS[s];
  return 'NEW';
}

/** Get display config (label/color/bg/dot) for a status string */
export function getStatusConfig(status) {
  return STATUS_CONFIG[normaliseStatus(status)] || STATUS_CONFIG.NEW;
}

/** Index of a status within the main flow (-1 if it's a side-status) */
export function statusIndex(status) {
  return STATUS_FLOW.indexOf(normaliseStatus(status));
}

/** True once a ticket has reached RESOLVED or CLOSED */
export function isTicketDone(status) {
  const s = normaliseStatus(status);
  return s === 'RESOLVED' || s === 'CLOSED';
}

/** True while sitting in either validation stage */
export function isInValidation(status) {
  const s = normaliseStatus(status);
  return s === 'CE_VALIDATION' || s === 'MANAGER_VALIDATION';
}

// Vendor-app sub-stages (within ASSIGNED_TO_VENDOR / IN_PROGRESS / WAITING_FOR_PARTS)
// kept separate because they're finer-grained than the ticket-level status.
export const VENDOR_STAGES = [
  'ACCEPTED',
  'TRAVELLING',
  'REACHED_SITE',
  'INSPECTION_COMPLETED',
  'WORK_STARTED',
  'WAITING_FOR_SPARE',
  'WORK_COMPLETED',
];

export const VENDOR_STAGE_LABELS = {
  ACCEPTED:               'Accepted',
  TRAVELLING:             'Travelling',
  REACHED_SITE:           'Reached Site',
  INSPECTION_COMPLETED:   'Inspection Completed',
  WORK_STARTED:           'Work Started',
  WAITING_FOR_SPARE:      'Waiting for Spare',
  WORK_COMPLETED:         'Work Completed',
};
